// // src/hooks/useDonation.ts
// import { useState } from 'react';
// import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
// import { donationService } from '../services/api/donationService';
// import { Donor } from '../types/donor';

// export const useDonation = () => {
//     const stripe = useStripe();
//     const elements = useElements();
//     const [loading, setLoading] = useState(false);
//     const [error, setError] = useState<string | null>(null);

//     const processDonation = async (donationData: Partial<Donor>) => {
//         if (!stripe || !elements) {
//             setError('Stripe has not been initialized');
//             return null;
//         }

//         try {
//             setLoading(true);
//             setError(null);

//             // Create payment intent
//             const { data: { clientSecret, customerId } } = await donationService.createPaymentIntent(
//                 donationData.amount!,
//                 donationData
//             );

//             // Confirm card payment
//             const cardElement = elements.getElement(CardElement);
//             if (!cardElement) throw new Error('Card element not found');

//             const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
//                 clientSecret,
//                 {
//                     payment_method: {
//                         card: cardElement,
//                         billing_details: {
//                             name: `${donationData.firstName} ${donationData.lastName}`,
//                             email: donationData.email,
//                             phone: donationData.phone,
//                         }
//                     }
//                 }
//             );

//             if (stripeError) {
//                 throw new Error(stripeError.message);
//             }

//             // Save donation to database
//             if (paymentIntent?.status === 'succeeded') {
//                 await donationService.saveDonation({
//                     ...donationData,
//                     paymentStatus: 'succeeded',
//                     stripeCustomerId: customerId, // Use the customerId from the API response
//                     lastPaymentDate: new Date().toISOString()
//                 });
//             }

//             return paymentIntent;

//         } catch (err: any) {
//             setError(err.message || 'An error occurred processing your donation');
//             return null;
//         } finally {
//             setLoading(false);
//         }
//     };

//     return {
//         processDonation,
//         loading,
//         error
//     };
// };

import { useState, useEffect, useCallback, useRef } from 'react';
import { donationService } from '../services/api/donationService';
import { clearCachedDonations } from '../cache/donationCache';
import { DonationListResponse, GetDonationsParams } from '../types/donation';
import { useAuth } from '../contexts/AuthContext';

export const useDonations = (options: {
  forceRefresh?: boolean;
  filter?: GetDonationsParams;
  perPage?: number;
  pageNumber?: number;
}) => {
  const [donations, setDonations] = useState<any[]>([]);
  const [totalDonations, setTotalDonations] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const { tokens } = useAuth();
  
  // Use a ref to track if we're currently fetching to prevent duplicate requests
  const isFetchingRef = useRef(false);
  // Track if component is mounted
  const isMountedRef = useRef(true);
  // Track last fetch time to prevent too frequent refreshes
  const lastFetchTimeRef = useRef(0);
  
  // Store options as ref to prevent dependency changes
  const optionsRef = useRef(options);
  // Extract options with defaults
  const { forceRefresh = false, filter = {}, perPage = 10, pageNumber = 1 } = options;
  
  // Update options ref when options change (but don't make this a dependency)
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);
  
  // Use a ref to track if we need to refetch on mount
  const shouldFetchOnMount = useRef(true);
  // Use a ref to track the initial force refresh value
  const initialForceRefresh = useRef(forceRefresh);

  const fetchDonations = useCallback(async (shouldForceRefresh: boolean) => {
    // Prevent fetching if we're already in progress
    if (isFetchingRef.current) {
      //console.log('Donations fetch already in progress, skipping duplicate request');
      return;
    }

    // Don't fetch if we don't have a token
    if (!tokens?.accessToken) {
      console.error('No access token available');
      if (isMountedRef.current) {
        setError(new Error('Authentication required'));
        setLoading(false);
      }
      return;
    }
    
    // Throttle rapid refresh requests
    const now = Date.now();
    const minRefreshInterval = 3000; // 3 seconds
    if (shouldForceRefresh && now - lastFetchTimeRef.current < minRefreshInterval) {
      //console.log('Throttling rapid refresh requests');
      return;
    }

    try {
      // Mark that we're fetching to prevent concurrent requests
      isFetchingRef.current = true;
      lastFetchTimeRef.current = now;
      
      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }

      if (shouldForceRefresh) {
        //console.log('Forcing refresh of donations data, clearing cache first');
        try {
          clearCachedDonations();
        } catch (cacheError) {
          console.error('Error clearing cache:', cacheError);
        }
      }

      // Get current options from ref to ensure values are stable
      const currentOptions = optionsRef.current;
      const params: GetDonationsParams = {
        ...currentOptions.filter,
        limit: currentOptions.perPage || perPage
      };

      // Add timeout for the API call to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timed out')), 15000);
      });

      const fetchPromise = donationService.getDonations(
        tokens.accessToken, 
        params, 
        shouldForceRefresh
      );

      // Race the fetch against a timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]) as DonationListResponse;

      // Check if component is still mounted before updating state
      if (!isMountedRef.current) {
        //console.log('Component unmounted during fetch, skipping state update');
        return;
      }

      if (!response) {
        throw new Error('Empty response from server');
      }

      // Safely extract the data
      const donationsList = response.donations || [];
      const count = response.count || 0;
      
      // Check again if component is mounted before updating state
      if (isMountedRef.current) {
        setDonations(donationsList);
        setTotalDonations(count);
        
        // Calculate total pages based on count
        const calculatedTotalPages = Math.ceil((count || 0) / (currentOptions.perPage || perPage));
        setTotalPages(calculatedTotalPages);
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Error fetching donations:', err);
      
      // Only update state if still mounted
      if (isMountedRef.current) {
        setError(err);
        setDonations([]);
        setTotalDonations(0);
        setLoading(false);
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, [tokens?.accessToken]); // Only depend on tokens to prevent loops

  // Initial fetch effect
  useEffect(() => {
    isMountedRef.current = true;
    
    // Only fetch on mount once
    if (shouldFetchOnMount.current && tokens?.accessToken) {
      shouldFetchOnMount.current = false;
      const initialRefresh = initialForceRefresh.current;
      
      fetchDonations(initialRefresh).catch(err => {
        console.error("Error in initial fetchDonations:", err);
        if (isMountedRef.current) {
          setLoading(false);
        }
      });
    } else if (!tokens?.accessToken) {
      setLoading(false);
    }
    
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchDonations, tokens?.accessToken]);
  
  // Handle force refresh changes separately
  useEffect(() => {
    // Skip the initial render since we handle it in the mount effect
    if (!shouldFetchOnMount.current && forceRefresh && tokens?.accessToken) {
      fetchDonations(true).catch(err => {
        console.error("Error in forceRefresh fetchDonations:", err);
        if (isMountedRef.current) {
          setLoading(false);
        }
      });
    }
  }, [forceRefresh, fetchDonations, tokens?.accessToken]);

  const refreshDonations = useCallback(() => {
    if (loading) {
      //console.log('Already loading, skipping refresh request');
      return Promise.resolve();
    }
    return fetchDonations(true);
  }, [fetchDonations, loading]);

  return {
    donations: donations || [],
    totalDonations,
    totalPages,
    loading,
    error,
    refreshDonations
  };
};
